import * as React from 'react';
import { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Skeleton,
  Tabs,
  Tab,
} from '@mui/material';
import ReceiptIcon from '@mui/icons-material/Receipt';
import EuroIcon from '@mui/icons-material/Euro';
import WarningIcon from '@mui/icons-material/Warning';
import { withTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import MetricCard from '../components/MetricCard';
import ExportButtons from '../components/ExportButtons';
import InvoiceDetailSection from './InvoiceDetailSection';
import ReceivablesSection from './ReceivablesSection';
import { useAccountingReport } from '../hooks/useReportsApi';

const formatCurrency = (value) => {
  if (value === null || value === undefined) return '-';
  return new Intl.NumberFormat('fi-FI', {
    style: 'currency',
    currency: 'EUR',
  }).format(value);
};

// ?subtab= values by sub-tab index; the overview has none.
const SUB_TABS = [null, 'invoiceDetail', 'receivables'];
const subTabIndex = (param) => Math.max(SUB_TABS.indexOf(param || null), 0);

function AccountingTab({ t, year, month }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [subTab, setSubTab] = useState(() => subTabIndex(searchParams.get('subtab')));
  // The overview's figures; the other sub-tabs fetch their own.
  const { data, loading, error } = useAccountingReport(year, month, { enabled: subTab === 0 });

  useEffect(() => {
    const newSubTab = subTabIndex(searchParams.get('subtab'));
    if (newSubTab !== subTab) {
      setSubTab(newSubTab);
    }
  }, [searchParams]);

  const handleSubTabChange = (e, newValue) => {
    setSubTab(newValue);
    const newParams = { tab: 'accounting' };
    if (SUB_TABS[newValue]) {
      newParams.subtab = SUB_TABS[newValue];
    }
    setSearchParams(newParams, { replace: true });
  };

  if (error && subTab === 0) {
    return <Alert severity="error">{t('reports.error')}: {error}</Alert>;
  }

  return (
    <Box>
      {/* Subtab toggle + Export */}
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
        <Tabs
          value={subTab}
          onChange={handleSubTabChange}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 500,
              minWidth: 'auto',
              px: 2,
            },
          }}
        >
          <Tab label={t('reports.accounting.overview')} />
          <Tab label={t('reports.accounting.invoiceDetail')} />
          <Tab label={t('reports.receivables.title')} data-testid="accounting-subtab-receivables" />
        </Tabs>
        {subTab === 0 && (
          <ExportButtons reportType="accounting" params={{ year, month }} />
        )}
        {subTab === 2 && (
          <ExportButtons reportType="receivables" params={{ year, month }} testIdPrefix="receivables-export" />
        )}
      </Box>

      {subTab === 2 ? (
        <ReceivablesSection year={year} month={month} />
      ) : subTab === 1 ? (
        <InvoiceDetailSection year={year} month={month} />
      ) : (
      <>

      {/* Header Info */}
      <Paper elevation={0} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
          {t('reports.accounting.title')} - {data?.month_name} {data?.year}
        </Typography>
        {loading ? (
          <Skeleton variant="text" width={300} />
        ) : (
          <>
            <Typography variant="body1" color="text.secondary">
              {data?.organization_name}
            </Typography>
            {data?.organization_business_id && (
              <Typography variant="body2" color="text.secondary">
                {t('reports.accounting.businessId')}: {data.organization_business_id}
              </Typography>
            )}
          </>
        )}
      </Paper>

      {/* KPI Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={4}>
          <MetricCard
            icon={EuroIcon}
            label={t('reports.accounting.totalInvoiced')}
            value={data?.total_invoiced}
            loading={loading}
            format="currency"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <MetricCard
            icon={ReceiptIcon}
            label={t('reports.accounting.invoiceCount')}
            value={data?.invoice_count}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <MetricCard
            icon={EuroIcon}
            label={t('reports.accounting.avgInvoice')}
            value={data?.average_invoice}
            loading={loading}
            format="currency"
          />
        </Grid>
      </Grid>

      {/* VAT Breakdown */}
      <Paper elevation={0} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          {t('reports.accounting.vatBreakdown')}
        </Typography>
        {loading ? (
          <Skeleton variant="rectangular" height={200} />
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell sx={{ fontWeight: 600 }}>{t('reports.accounting.vatRate')}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>{t('reports.accounting.exclVat')}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>{t('reports.accounting.vat')}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>{t('reports.accounting.inclVat')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(data?.vat_breakdown || []).map((item, index) => (
                  <TableRow key={index}>
                    <TableCell>{item.rate_display}</TableCell>
                    <TableCell align="right">{formatCurrency(item.excl_vat)}</TableCell>
                    <TableCell align="right">{formatCurrency(item.vat)}</TableCell>
                    <TableCell align="right">{formatCurrency(item.incl_vat)}</TableCell>
                  </TableRow>
                ))}
                {data?.vat_totals && (
                  <TableRow sx={{ bgcolor: 'grey.100' }}>
                    <TableCell sx={{ fontWeight: 700 }}>{t('reports.accounting.total')}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>{formatCurrency(data.vat_totals.excl_vat)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>{formatCurrency(data.vat_totals.vat)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>{formatCurrency(data.vat_totals.incl_vat)}</TableCell>
                  </TableRow>
                )}
                {/* Margin-VAT-scheme sales: reconciliation below the declarable YHTEENSÄ
                    (never folded into it — margin VAT must stay out of the VAT declaration),
                    followed by a turnover total that ties to Maksutavoittain / Laskutettu yhteensä.
                    Margin sales are VAT-free in reports (the accountant computes the margin VAT
                    separately), so both rows carry a value only in the Yhteensä column. */}
                {data?.margin_vat && (
                  <>
                    <TableRow>
                      <TableCell>{t('reports.accounting.marginVatSales')}</TableCell>
                      <TableCell />
                      <TableCell />
                      <TableCell align="right">{formatCurrency(data.margin_vat.incl_vat)}</TableCell>
                    </TableRow>
                    <TableRow sx={{ bgcolor: 'grey.100' }}>
                      <TableCell sx={{ fontWeight: 700 }}>{t('reports.accounting.turnoverTotal')}</TableCell>
                      <TableCell />
                      <TableCell />
                      <TableCell align="right" sx={{ fontWeight: 700 }}>{formatCurrency(data.total_invoiced)}</TableCell>
                    </TableRow>
                  </>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Sales Breakdown: Työt / Varaosat / Muut with the same split as the Liikevaihto
          report (order discount netted into labor and parts, other custom rows under Muut),
          so Työt's Veroton equals that report's labor figure. The rows and YHTEENSÄ are
          declarable-only and tie to the ALV-erittely YHTEENSÄ; margin-scheme sales follow
          it gross-only on their own row, exactly like in the ALV-erittely above. */}
      <Paper elevation={0} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          {t('reports.accounting.salesBreakdown')}
        </Typography>
        {loading ? (
          <Skeleton variant="rectangular" height={150} />
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell sx={{ fontWeight: 600 }}>{t('reports.accounting.category')}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>{t('reports.accounting.exclVat')}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>{t('reports.accounting.vat')}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>{t('reports.accounting.inclVat')}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>{t('reports.accounting.percent')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(data?.sales_breakdown || []).map((item) => (
                  <TableRow key={item.category}>
                    <TableCell>
                      {t(`reports.accounting.categories.${item.category}`, { defaultValue: item.category_display })}
                    </TableCell>
                    <TableCell align="right">{formatCurrency(item.excl_vat)}</TableCell>
                    <TableCell align="right">{formatCurrency(item.vat)}</TableCell>
                    <TableCell align="right">{formatCurrency(item.incl_vat)}</TableCell>
                    <TableCell align="right">{item.percent.toFixed(1)} %</TableCell>
                  </TableRow>
                ))}
                {data?.sales_totals && (
                  <TableRow sx={{ bgcolor: 'grey.100' }}>
                    <TableCell sx={{ fontWeight: 700 }}>{t('reports.accounting.total')}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>{formatCurrency(data.sales_totals.excl_vat)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>{formatCurrency(data.sales_totals.vat)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>{formatCurrency(data.sales_totals.incl_vat)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>{data.sales_totals.percent.toFixed(1)} %</TableCell>
                  </TableRow>
                )}
                {data?.margin_vat && (
                  <>
                    <TableRow>
                      <TableCell>{t('reports.accounting.marginVatSales')}</TableCell>
                      <TableCell />
                      <TableCell />
                      <TableCell align="right">{formatCurrency(data.margin_vat.incl_vat)}</TableCell>
                      <TableCell />
                    </TableRow>
                    <TableRow sx={{ bgcolor: 'grey.100' }}>
                      <TableCell sx={{ fontWeight: 700 }}>{t('reports.accounting.salesTotalWithMargin')}</TableCell>
                      <TableCell />
                      <TableCell />
                      <TableCell align="right" sx={{ fontWeight: 700 }}>{formatCurrency(data.sales_total_with_margin)}</TableCell>
                      <TableCell />
                    </TableRow>
                  </>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* By Customer Type */}
      <Paper elevation={0} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          {t('reports.accounting.byCustomerType')}
        </Typography>
        {loading ? (
          <Skeleton variant="rectangular" height={150} />
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell sx={{ fontWeight: 600 }}>{t('reports.accounting.customerType')}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>{t('reports.accounting.amount')}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>{t('reports.accounting.invoiceCount')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(data?.by_customer_type || []).map((item, index) => (
                  <TableRow key={index}>
                    <TableCell>{item.type_display}</TableCell>
                    <TableCell align="right">{formatCurrency(item.amount)}</TableCell>
                    <TableCell align="right">{item.invoice_count}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* By Payment Method */}
      <Paper elevation={0} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          {t('reports.accounting.byPaymentMethod')}
        </Typography>
        {loading ? (
          <Skeleton variant="rectangular" height={150} />
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell sx={{ fontWeight: 600 }}>{t('reports.accounting.paymentMethodColumn')}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>{t('reports.accounting.exclVat')}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>{t('reports.accounting.vat')}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>{t('reports.accounting.inclVat')}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>{t('reports.accounting.invoiceCount')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(data?.by_payment_method?.rows || []).map((item, index) => (
                  <TableRow key={index}>
                    <TableCell>{item.label}</TableCell>
                    <TableCell align="right">{formatCurrency(item.excl_vat)}</TableCell>
                    <TableCell align="right">{formatCurrency(item.vat)}</TableCell>
                    <TableCell align="right">{formatCurrency(item.incl_vat)}</TableCell>
                    <TableCell align="right">{item.count}</TableCell>
                  </TableRow>
                ))}
                {data?.by_payment_method?.totals && (
                  <TableRow sx={{ bgcolor: 'grey.100' }}>
                    <TableCell sx={{ fontWeight: 700 }}>{t('reports.accounting.total')}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>{formatCurrency(data.by_payment_method.totals.excl_vat)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>{formatCurrency(data.by_payment_method.totals.vat)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>{formatCurrency(data.by_payment_method.totals.incl_vat)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>{data.by_payment_method.totals.count}</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Cash/card taken by hand against invoices issued earlier (osasuoritus, merkitse
          maksetuksi), by the day the money arrived. Not part of the turnover above, and
          rendered only when the month has one so the report keeps its usual shape. */}
      {!loading && (data?.later_payments || []).length > 0 && (
        <Paper elevation={0} sx={{ p: 3, mb: 3 }} data-testid="accounting-later-payments">
          <Typography variant="h6" sx={{ mb: 0.5, fontWeight: 600 }}>
            {t('reports.cashRegister.laterPayments')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('reports.cashRegister.laterPaymentsNote')}
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell sx={{ fontWeight: 600 }}>{t('reports.cashRegister.date')}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{t('reports.cashRegister.day')}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>{t('reports.cashRegister.cash')}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>{t('reports.cashRegister.card')}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>{t('reports.cashRegister.total')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.later_payments.map((day) => {
                  const dateParts = day.date.split('-');
                  const displayDate = `${parseInt(dateParts[2])}.${parseInt(dateParts[1])}.${dateParts[0]}`;
                  return (
                    <TableRow key={day.date}>
                      <TableCell>{displayDate}</TableCell>
                      <TableCell>{day.day_name}</TableCell>
                      <TableCell align="right">{formatCurrency(day.cash)}</TableCell>
                      <TableCell align="right">{formatCurrency(day.card)}</TableCell>
                      <TableCell align="right">{formatCurrency(day.total)}</TableCell>
                    </TableRow>
                  );
                })}
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell colSpan={2} sx={{ fontWeight: 700 }}>{t('reports.accounting.total')}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>{formatCurrency(data.later_payments_cash_total || 0)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>{formatCurrency(data.later_payments_card_total || 0)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>{formatCurrency(data.later_payments_total || 0)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Open Invoices */}
      <Paper elevation={0} sx={{ p: 3, bgcolor: data?.open_invoices_count > 0 ? 'warning.50' : 'background.paper' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          {data?.open_invoices_count > 0 && <WarningIcon color="warning" />}
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {t('reports.accounting.openInvoices')}
          </Typography>
        </Box>
        {loading ? (
          <Skeleton variant="rectangular" height={80} />
        ) : (
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {t('reports.accounting.openInvoicesCount')}
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                {data?.open_invoices_count || 0}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {t('reports.accounting.openInvoicesTotal')}
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 700, color: data?.open_invoices_total > 0 ? 'warning.main' : 'text.primary' }}>
                {formatCurrency(data?.open_invoices_total || 0)}
              </Typography>
            </Grid>
          </Grid>
        )}
      </Paper>
      </>
      )}
    </Box>
  );
}

export default withTranslation()(AccountingTab);
